<template>
  <div
    class="debug-inspector"
    :class="{ 'debug-inspector--active': !isProd }"
    @mouseover="onMouseOver"
    @mouseleave="onMouseLeave"
    @click.capture="onCapture"
  >
    <slot />
    <transition name="debug-fade">
      <div
        v-if="(show || pinned) && !isProd"
        class="debug-inspector__panel"
        :class="{
          'debug-inspector__panel--pinned': pinned,
          'debug-inspector__panel--dragging': dragging
        }"
        :style="panelStyle"
        ref="panel"
      >
        <div
          class="debug-inspector__header"
          :style="pinned ? { cursor: dragging ? 'grabbing' : 'grab' } : {}"
          @mousedown="onDragStart"
        >
          <span>
            🐛 {{ label }}
            <span v-if="!pinned" class="debug-inspector__hint"
              >ctrl+click to pin</span
            >
            <span v-else class="debug-inspector__hint">drag to move</span>
          </span>
          <div class="debug-inspector__header-actions">
            <span class="debug-inspector__env">{{ env }}</span>
            <button
              class="debug-inspector__close"
              @mousedown.stop
              @click.stop="close"
            >
              ✕
            </button>
          </div>
        </div>
        <div class="debug-inspector__json">
          <VueJsonPretty
            :data="localData"
            :deep="deep"
            :indent="4"
            :show-length="true"
            :show-line="true"
            :show-line-number="true"
            :show-icon="true"
            :show-double-quotes="true"
            :collapsed-on-click-brackets="true"
            :editable="pinned"
            editable-trigger="dblclick"
            theme="dark"
            @update:data="
              (val) => {
                localData = val;
                $emit('update:data', val);
              }
            "
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import CommonHelper from "@/helpers/common.helper.js";
import {
  debugState,
  raiseToTop,
  createId,
  setActive
} from "@/helpers/debug.state.js";
import VueJsonPretty from "vue-json-pretty";
import "vue-json-pretty/lib/styles.css";

export default {
  name: "DebugInspector",
  components: { VueJsonPretty },
  emits: ["update:data"],
  props: {
    data: {
      required: true
    },
    label: {
      type: String,
      default: "Debug"
    },
    deep: {
      type: Number,
      default: 3
    }
  },
  data() {
    return {
      show: false,
      pinned: false,
      dragging: false,
      panelX: null,
      panelY: null,
      dragOffsetX: 0,
      dragOffsetY: 0,
      zIndex: 10000,
      localData: null,
      hideTimer: null,
      id: createId()
    };
  },
  watch: {
    "debugState.activeId"(activeId) {
      if (!this.pinned && activeId !== this.id) {
        clearTimeout(this.hideTimer);
        this.show = false;
      }
    },
    data: {
      immediate: true,
      deep: true,
      handler(val) {
        this.localData = JSON.parse(JSON.stringify(val ?? null));
      }
    }
  },
  computed: {
    debugState() {
      return debugState;
    },
    isProd() {
      return CommonHelper.isProd() || !debugState.enabled;
    },
    env() {
      return localStorage.getItem("apiEnv") || "test";
    },
    panelStyle() {
      if (this.panelX !== null && this.panelY !== null) {
        return {
          position: "fixed",
          left: this.panelX + "px",
          top: this.panelY + "px",
          margin: 0,
          zIndex: this.zIndex
        };
      }
      return this.pinned ? { zIndex: this.zIndex } : {};
    }
  },
  methods: {
    unpin() {
      this.pinned = false;
      this.show = false;
      this.panelX = null;
      this.panelY = null;
    },
    close() {
      if (this.pinned) {
        this.unpin();
      } else {
        this.show = false;
      }
    },
    onMouseOver(event) {
      if (this.isProd || this.pinned) return;
      // If hovering inside a nested debug-inspector, hide this panel
      let el = event.target;
      while (el && el !== this.$el) {
        if (el.classList && el.classList.contains("debug-inspector")) {
          this.scheduleHide();
          return;
        }
        el = el.parentElement;
      }
      clearTimeout(this.hideTimer);
      this.show = true;
      setActive(this.id);
    },
    onMouseLeave() {
      if (this.isProd || this.pinned) return;
      this.scheduleHide();
    },
    scheduleHide() {
      clearTimeout(this.hideTimer);
      this.hideTimer = setTimeout(() => {
        this.show = false;
      }, 300);
    },
    onCapture(event) {
      if (!this.isProd && event.ctrlKey) {
        // If the click came from inside a nested debug-inspector, let that one handle it
        let el = event.target;
        while (el && el !== this.$el) {
          if (el.classList && el.classList.contains("debug-inspector")) {
            return;
          }
          el = el.parentElement;
        }
        event.stopPropagation();
        event.preventDefault();
        this.pinned = !this.pinned;
        this.show = this.pinned;
        if (this.pinned) {
          this.zIndex = raiseToTop();
        } else {
          this.panelX = null;
          this.panelY = null;
        }
      }
    },
    onDragStart(event) {
      if (!this.pinned) return;
      event.preventDefault();
      this.zIndex = raiseToTop();
      const panel = this.$refs.panel;
      const rect = panel.getBoundingClientRect();
      this.dragOffsetX = event.clientX - rect.left;
      this.dragOffsetY = event.clientY - rect.top;
      this.panelX = rect.left;
      this.panelY = rect.top;
      this.dragging = true;
      window.addEventListener("mousemove", this.onDragMove);
      window.addEventListener("mouseup", this.onDragEnd);
    },
    onDragMove(event) {
      this.panelX = event.clientX - this.dragOffsetX;
      this.panelY = event.clientY - this.dragOffsetY;
    },
    onDragEnd() {
      this.dragging = false;
      window.removeEventListener("mousemove", this.onDragMove);
      window.removeEventListener("mouseup", this.onDragEnd);
    }
  },
  beforeUnmount() {
    clearTimeout(this.hideTimer);
    window.removeEventListener("mousemove", this.onDragMove);
    window.removeEventListener("mouseup", this.onDragEnd);
  }
};
</script>

<style lang="scss">
.debug-fade-enter-active {
  transition: opacity 0.15s ease;
}
.debug-fade-leave-active {
  transition: opacity 0.25s ease;
}
.debug-fade-enter-from,
.debug-fade-leave-to {
  opacity: 0;
}

.debug-inspector {
  &--active {
    position: relative;
  }

  &__panel {
    position: absolute;
    z-index: 9999;
    top: 0;
    left: 100%;
    margin-left: 8px;
    width: 420px;
    max-height: 480px;
    background: #1e1e2e;
    border: 1px solid #45475a;
    border-radius: 6px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
    display: flex;
    flex-direction: column;

    &--pinned {
      border-color: #fab387;
      resize: both;
      overflow: hidden;
      min-width: 280px;
      min-height: 120px;
      max-height: none;
      height: 480px;
    }

    &--dragging {
      user-select: none;
    }

    @media (max-width: 1400px) {
      left: auto;
      right: 0;
      margin-left: 0;
    }
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 10px;
    background: #313244 !important;
    border-radius: 6px 6px 0 0;
    font-family: monospace;
    font-size: 0.75rem;
    color: #cdd6f4 !important;
    flex-shrink: 0;

    * {
      color: #cdd6f4 !important;
    }
  }

  &__hint {
    margin-left: 6px;
    font-size: 0.65rem;
    opacity: 0.45;
    font-style: italic;
    color: #cdd6f4 !important;
  }

  &__header-actions {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__close {
    background: none;
    border: none;
    color: #cdd6f4 !important;
    cursor: pointer;
    font-size: 0.8rem;
    line-height: 1;
    padding: 2px 4px;
    border-radius: 3px;
    opacity: 0.6;
    transition:
      opacity 0.1s,
      background 0.1s;

    &:hover {
      opacity: 1;
      background: rgba(243, 139, 168, 0.2);
      color: #f38ba8 !important;
    }
  }

  &__env {
    background: #fab387 !important;
    color: #1e1e2e !important;
    border-radius: 3px;
    padding: 1px 6px;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
  }

  &__json {
    margin: 0;
    padding: 10px;
    overflow: auto;
    font-family: monospace;
    font-size: 0.72rem;
    line-height: 1.5;
    background: #1e1e2e !important;
    flex: 1;

    .vjs-tree {
      background: transparent !important;
      color: #cdd6f4 !important;
      font-size: 0.72rem !important;
    }

    .vjs-key {
      color: #89b4fa !important;
    }

    .vjs-value-string {
      color: #a6e3a1 !important;
    }

    .vjs-value-number {
      color: #fab387 !important;
    }

    .vjs-value-boolean {
      color: #cba6f7 !important;
    }

    .vjs-value-null {
      color: #f38ba8 !important;
    }

    .vjs-tree__node:hover {
      background: rgba(255, 255, 255, 0.05) !important;
    }

    .vjs-line-number {
      color: #585b70 !important;
      min-width: 2em;
    }
  }
}
</style>
